import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from "@remix-run/react";
import type { LinksFunction, LoaderFunctionArgs } from "@remix-run/cloudflare";
import { json } from "@remix-run/cloudflare";

import "./tailwind.css";
import { Header } from "~/components/layout/Header";
import { Footer } from "~/components/layout/Footer";
import { UserProvider } from "~/lib/user-context";
import { ErrorBoundary } from "~/components/ui/error-boundary";

export async function loader({ context }: LoaderFunctionArgs) {
  // 在 Cloudflare Workers 中，环境变量通过 context.cloudflare.env 传递
  const env = (context as any)?.cloudflare?.env || {}
  
  return json({
    ENV: {
      SUPABASE_URL: env.SUPABASE_URL || process.env.SUPABASE_URL || '',
      SUPABASE_ANON_KEY: env.SUPABASE_ANON_KEY || process.env.SUPABASE_ANON_KEY || '',
    }
  })
}

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
  // PWA Manifest
  { rel: "manifest", href: "/manifest.json" },
  // Favicons - 统一使用 favicon.ico
  { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
  { rel: "icon", type: "image/x-icon", sizes: "32x32", href: "/favicon.ico" },
  { rel: "icon", type: "image/x-icon", sizes: "16x16", href: "/favicon.ico" },
  { rel: "apple-touch-icon", href: "/favicon.ico" },
];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />

        <style dangerouslySetInnerHTML={{
          __html: `
            .typing-effect {
              overflow: hidden;
              white-space: nowrap;
              animation: typing 2s steps(40, end);
            }
            
            @keyframes typing {
              from { width: 0; }
              to { width: 100%; }
            }
            
            .cyber-glow {
              box-shadow: 0 0 10px rgba(34, 197, 94, 0.5);
            }
          `
        }} />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
        
        {/* 将第三方脚本移到页面底部，在 React hydration 之后加载 */}
        <script dangerouslySetInnerHTML={{
          __html: `
            (function() {
              // 延迟加载 Google Tag Manager，避免干扰 hydration
              setTimeout(function() {
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','GTM-TGVWQWBK');
              }, 1000);
            })();
          `
        }} />

        {/* Google Tag Manager (noscript) - 保持在 body 顶部 */}
        <noscript>
          <iframe 
            src="https://www.googletagmanager.com/ns.html?id=GTM-TGVWQWBK"
            height="0" 
            width="0" 
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
      </body>
    </html>
  );
}

export default function App() {
  const data = useLoaderData<typeof loader>()
  
  return (
    <>
      <script
        dangerouslySetInnerHTML={{
          __html: `window.ENV = ${JSON.stringify(data.ENV)};`,
        }}
      />
      <ErrorBoundary>
        <UserProvider>
          <div className="min-h-screen flex flex-col">
            <ErrorBoundary>
              <Header />
            </ErrorBoundary>
            <main className="flex-1">
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </main>
            <ErrorBoundary>
              <Footer />
            </ErrorBoundary>
          </div>
        </UserProvider>
      </ErrorBoundary>
    </>
  );
}
